import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';
import {
  Repossession,
  RepossessionDecision,
  RepossessionOpsRecommendation,
  RepossessionRecoveryCostType,
  RepossessionSettlementType,
  RepossessionSeverity,
  RepossessionStatusHistoryApi,
} from '@/types/common';

export interface RepossessionListRequest {
  size?: string;
  page?: string;
  search?: string;
  statuses?: string;
  severities?: string;
  /** One unit's history (vehicle detail) — matches on the handover's vehicle. */
  vehicleID?: string;
  startDate?: string;
  endDate?: string;
}

export interface RepossessionListResponse {
  data: Repossession[];
  pagination: {
    total: number;
    page: number;
    size: number;
    lastPage: number;
  };
}

export interface CreateRepossessionRequest {
  handoverID: string;
  reason: string;
  notes: string;
  assignedOpsTeam: string;
  createdBy: string;
  severity: RepossessionSeverity;
}

export interface UpdateRepossessionRequest {
  handoverID?: string;
  reason?: string;
  notes?: string;
  assignedOpsTeam?: string;
  updatedBy: string;
  severity?: RepossessionSeverity;
}

export interface RepossessionResponse {
  message: string;
  data?: Repossession;
}

export interface RepossessionStatusHistoryResponse {
  data: RepossessionStatusHistoryApi[];
}

export const getListRepossessions = async (
  params?: RepossessionListRequest
): Promise<RepossessionListResponse> => {
  try {
    const response = await Axios.get('/v1/repossessions', { params });
    return response.data;
  } catch (error: unknown) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

export const getRepossessionById = async (
  id: string
): Promise<Repossession> => {
  try {
    const response = await Axios.get(`/v1/repossessions/${id}`);
    return response.data.data || response.data; // Handle different response structures
  } catch (error: unknown) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

export const getRepossessionStatusHistory = async (
  id: string
): Promise<RepossessionStatusHistoryApi[]> => {
  try {
    const response = await Axios.get(`/v1/repossessions/${id}/status-history`);
    const data = response.data.data || response.data;
    return Array.isArray(data) ? data : [];
  } catch (error: unknown) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

/** What POST /v1/repossessions hands back — enough to land on the case. */
export interface CreatedRepossession {
  ID: string;
  code: string;
}

export const createRepossession = async (
  data: CreateRepossessionRequest
): Promise<CreatedRepossession | null> => {
  try {
    const response = await Axios.post<{ data?: CreatedRepossession }>(
      '/v1/repossessions',
      data
    );
    // Null on a backend that predates the created-case response — callers
    // fall back to the list instead of a broken detail URL.
    return response.data?.data ?? null;
  } catch (error: unknown) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

export const updateRepossession = async (
  id: string,
  data: UpdateRepossessionRequest
): Promise<RepossessionResponse> => {
  try {
    const response = await Axios.patch(`/v1/repossessions/${id}`, data);
    return response.data;
  } catch (error: unknown) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

export interface MarkRepossessionRequest {
  updatedBy: string;
}

export const escalateRepossessionToPotentialLoss = async (
  id: string,
  data: MarkRepossessionRequest
): Promise<RepossessionResponse> => {
  try {
    const response = await Axios.patch(
      `/v1/repossessions/${id}/escalate-to-potential-loss`,
      data
    );
    return response.data;
  } catch (error: unknown) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

export interface StartInvestigationRequest {
  reason: string;
  updatedBy: string;
  escalationHours: number;
}

export interface EscalateToManagerRequest {
  searchEfforts: string;
  escalationReason: string;
  lastKnownInfo: string;
  opsRecommendation: RepossessionOpsRecommendation;
  estimatedCost?: number;
  updatedBy: string;
}

export interface MakeDecisionRequest {
  decision: RepossessionDecision;
  decisionNotes: string;
  updatedBy: string;
}

export interface MarkAsFoundRequest {
  recoveryCost: number;
  recoveryCostType?: RepossessionRecoveryCostType;
  recoveryCostTypeOther?: string;
  recoveryNotes: string;
  updatedBy: string;
}

export interface ProcessSettlementRequest {
  settlementAmount: number;
  settlementType: RepossessionSettlementType;
  settlementNotes: string;
  settlementDocumentURL: string;
  financeApproval: boolean;
  updatedBy: string;
}

export interface CloseCaseRequest {
  updatedBy: string;
}

export const markRepossessionAsFound = async (
  id: string,
  data: MarkRepossessionRequest
): Promise<RepossessionResponse> => {
  try {
    const response = await Axios.patch(
      `/v1/repossessions/${id}/mark-as-found`,
      data
    );
    return response.data;
  } catch (error: unknown) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

export const markRepossessionAsLost = async (
  id: string,
  data: MarkRepossessionRequest
): Promise<RepossessionResponse> => {
  try {
    const response = await Axios.patch(
      `/v1/repossessions/${id}/mark-as-lost`,
      data
    );
    return response.data;
  } catch (error: unknown) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

export const startRepossessionInvestigation = async (
  id: string,
  data: StartInvestigationRequest
): Promise<RepossessionResponse> => {
  try {
    const response = await Axios.patch(
      `/v1/repossessions/${id}/start-investigation`,
      data
    );
    return response.data;
  } catch (error: unknown) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

export const escalateRepossessionToManager = async (
  id: string,
  data: EscalateToManagerRequest
): Promise<RepossessionResponse> => {
  try {
    const response = await Axios.patch(
      `/v1/repossessions/${id}/escalate-to-manager`,
      data
    );
    return response.data;
  } catch (error: unknown) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

export const makeRepossessionDecision = async (
  id: string,
  data: MakeDecisionRequest
): Promise<RepossessionResponse> => {
  try {
    const response = await Axios.patch(
      `/v1/repossessions/${id}/make-decision`,
      data
    );
    return response.data;
  } catch (error: unknown) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

export const markRepossessionAsFoundNew = async (
  id: string,
  data: MarkAsFoundRequest
): Promise<RepossessionResponse> => {
  try {
    const response = await Axios.patch(
      `/v1/repossessions/${id}/new-mark-as-found`,
      data
    );
    return response.data;
  } catch (error: unknown) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

export const processRepossessionSettlement = async (
  id: string,
  data: ProcessSettlementRequest
): Promise<RepossessionResponse> => {
  try {
    const response = await Axios.patch(
      `/v1/repossessions/${id}/process-settlement`,
      data
    );
    return response.data;
  } catch (error: unknown) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

export const closeRepossessionCase = async (
  id: string,
  data: CloseCaseRequest
): Promise<RepossessionResponse> => {
  try {
    const response = await Axios.patch(
      `/v1/repossessions/${id}/close-case`,
      data
    );
    return response.data;
  } catch (error: unknown) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};
